import { type Bone, Quaternion, Vector3 } from 'three'

export interface SwingTwistLimits {
  twist_axis?: 'x' | 'y' | 'z'
  twist?: { min_deg: number, max_deg: number }
  swing?: { max_deg: number }
}

/**
 * Cyclic Coordinate Descent (CCD) inverse-kinematics solver.
 *
 * For an IK target bone, iteratively rotates each bone in the parent chain
 * (from immediate parent up to `chain_length` ancestors) so that the IK
 * target's world position approaches a desired world position. Each iteration
 * sweeps the chain from end-effector toward the root, rotating each bone to
 * point its end-effector vector at the target.
 *
 * Cheap enough to run every drag frame; converges in typically <10 iterations
 * for small (≤4) chains. Doesn't handle pole vectors or joint limits — both
 * are deferred to a future pass per the Build Skeleton roadmap.
 *
 * Mutates the parent bones' `quaternion` fields directly. Caller is
 * responsible for capturing the new rotations and committing them to the
 * descriptor when the drag ends.
 */

const TMP_BONE_WORLD = new Vector3()
const TMP_END_WORLD = new Vector3()
const TMP_TO_END = new Vector3()
const TMP_TO_TARGET = new Vector3()
const TMP_ROT_AXIS = new Vector3()
const TMP_QUAT = new Quaternion()
const TMP_INVERSE_PARENT_QUAT = new Quaternion()

export interface IkSolveOptions {
  iterations?: number      // Max iterations per call (default 10).
  position_tolerance?: number // Stop when end-effector is within this world distance of target (default 1e-3).
  damping?: number         // Per-bone rotation step scale (0..1). 1.0 = full step (fast, can overshoot); lower values smooth out (default 0.5).
  pole_vector?: Vector3    // Optional world-space direction. After CCD converges, the chain is rotated around the root→end axis to align its bend plane with this direction.
  rotation_limits?: Map<Bone, SwingTwistLimits> // Per-bone swing-twist clamps applied after each per-bone CCD update.
}

export class IkSolver {
  /**
   * Run CCD on the chain ending at `ik_target_bone` (the end effector). The
   * chain consists of the bone's first `chain_length` ancestors (excluding
   * the IK target itself). The IK target's local position is NOT modified —
   * the parent rotations bend the chain to bring the IK target's head world
   * position to `target_world_position`.
   *
   * Returns the bones that were rotated (the parent chain, top-to-bottom),
   * so the caller can capture their new local quaternions for committing.
   */
  static solve_ccd (
    ik_target_bone: Bone,
    chain_length: number,
    target_world_position: Vector3,
    options: IkSolveOptions = {}
  ): Bone[] {
    const iterations = options.iterations ?? 10
    const position_tolerance = options.position_tolerance ?? 1e-3
    const damping = options.damping ?? 0.5
    const limits = options.rotation_limits

    // Walk up the parent chain to gather the bones we'll rotate.
    const chain: Bone[] = []
    let cursor: Bone | null = ik_target_bone.parent instanceof Object && (ik_target_bone.parent as Bone).isBone === true
      ? ik_target_bone.parent as Bone
      : null
    while (cursor !== null && chain.length < chain_length) {
      chain.push(cursor)
      cursor = cursor.parent instanceof Object && (cursor.parent as Bone).isBone === true ? cursor.parent as Bone : null
    }
    if (chain.length === 0) return []

    // Make sure all relevant world matrices are current before we begin.
    chain[chain.length - 1].updateMatrixWorld(true)

    for (let iter = 0; iter < iterations; iter++) {
      // Sweep: closest-to-end-effector first.
      for (let i = 0; i < chain.length; i++) {
        const bone = chain[i]
        bone.updateMatrixWorld(true)
        ik_target_bone.getWorldPosition(TMP_END_WORLD)
        bone.getWorldPosition(TMP_BONE_WORLD)

        TMP_TO_END.copy(TMP_END_WORLD).sub(TMP_BONE_WORLD)
        TMP_TO_TARGET.copy(target_world_position).sub(TMP_BONE_WORLD)

        const len_to_end = TMP_TO_END.length()
        const len_to_target = TMP_TO_TARGET.length()
        if (len_to_end < 1e-9 || len_to_target < 1e-9) continue

        TMP_TO_END.divideScalar(len_to_end)
        TMP_TO_TARGET.divideScalar(len_to_target)

        const dot = Math.max(-1, Math.min(1, TMP_TO_END.dot(TMP_TO_TARGET)))
        const angle = Math.acos(dot) * damping
        if (angle < 1e-6) continue

        TMP_ROT_AXIS.crossVectors(TMP_TO_END, TMP_TO_TARGET)
        if (TMP_ROT_AXIS.lengthSq() < 1e-12) continue
        TMP_ROT_AXIS.normalize()

        // Apply the world-space rotation to the bone's local quaternion.
        TMP_QUAT.setFromAxisAngle(TMP_ROT_AXIS, angle)
        if (bone.parent !== null) {
          bone.parent.getWorldQuaternion(TMP_INVERSE_PARENT_QUAT).invert()
          const parent_world = new Quaternion().copy(TMP_INVERSE_PARENT_QUAT).invert()
          const delta = new Quaternion().copy(TMP_INVERSE_PARENT_QUAT).multiply(TMP_QUAT).multiply(parent_world)
          bone.quaternion.premultiply(delta)
        } else {
          bone.quaternion.premultiply(TMP_QUAT)
        }

        if (limits !== undefined) {
          const bone_limits = limits.get(bone)
          if (bone_limits !== undefined) clamp_quaternion_to_limits(bone.quaternion, bone_limits)
        }
      }

      ik_target_bone.updateMatrixWorld(true)
      ik_target_bone.getWorldPosition(TMP_END_WORLD)
      if (TMP_END_WORLD.distanceTo(target_world_position) < position_tolerance) {
        break
      }
    }

    if (options.pole_vector !== undefined && chain.length >= 1) {
      apply_pole_alignment(chain, ik_target_bone, options.pole_vector)
      if (limits !== undefined) {
        for (const bone of chain) {
          const bone_limits = limits.get(bone)
          if (bone_limits !== undefined) clamp_quaternion_to_limits(bone.quaternion, bone_limits)
        }
        chain[chain.length - 1].updateMatrixWorld(true)
      }
    }

    return chain
  }

  /**
   * Clamp a local quaternion against swing-twist limits. Exported so it can
   * be reused by callers that enforce limits outside the IK solver (e.g.,
   * after a manual rotate-gizmo commit, or live during a gizmo drag).
   */
  static clamp_local_quaternion (quaternion: Quaternion, limits: SwingTwistLimits): void {
    clamp_quaternion_to_limits(quaternion, limits)
  }
}

/**
 * Swing-twist quaternion clamping. Decomposes `quaternion` into a twist
 * around the configured axis and a swing perpendicular to it, then clamps
 * each component independently. Mutates the input quaternion.
 *
 * The twist quaternion's signed angle is clamped to [twist.min_deg,
 * twist.max_deg]. The swing quaternion's rotation magnitude is clamped to
 * [0, swing.max_deg] (cone constraint). Either component may be omitted to
 * leave that direction unconstrained.
 */
const TMP_AXIS_VEC = new Vector3()
const TMP_TWIST = new Quaternion()
const TMP_TWIST_INV = new Quaternion()
const TMP_SWING = new Quaternion()
const TMP_NEW_TWIST = new Quaternion()
const TMP_NEW_SWING = new Quaternion()
const TMP_IDENTITY = new Quaternion()

function clamp_quaternion_to_limits (quaternion: Quaternion, limits: SwingTwistLimits): void {
  if (limits.twist === undefined && limits.swing === undefined) return

  set_axis(TMP_AXIS_VEC, limits.twist_axis ?? 'y')
  decompose_swing_twist(quaternion, TMP_AXIS_VEC, TMP_SWING, TMP_TWIST)

  // Clamp twist around the chosen axis.
  if (limits.twist !== undefined) {
    const twist_axis_component =
      limits.twist_axis === 'x' ? TMP_TWIST.x
      : limits.twist_axis === 'z' ? TMP_TWIST.z
      : TMP_TWIST.y
    // Signed twist angle around the axis (positive = same direction as axis).
    const sign = Math.sign(twist_axis_component) || 1
    const twist_angle = 2 * Math.atan2(Math.abs(twist_axis_component), TMP_TWIST.w) * sign
    const min_rad = limits.twist.min_deg * Math.PI / 180
    const max_rad = limits.twist.max_deg * Math.PI / 180
    const clamped = Math.max(min_rad, Math.min(max_rad, twist_angle))
    if (clamped !== twist_angle) {
      TMP_NEW_TWIST.setFromAxisAngle(TMP_AXIS_VEC, clamped)
    } else {
      TMP_NEW_TWIST.copy(TMP_TWIST)
    }
  } else {
    TMP_NEW_TWIST.copy(TMP_TWIST)
  }

  // Clamp swing magnitude (cone half-angle).
  if (limits.swing !== undefined) {
    const swing_w = Math.max(-1, Math.min(1, TMP_SWING.w))
    const swing_angle = 2 * Math.acos(Math.abs(swing_w))
    const max_rad = limits.swing.max_deg * Math.PI / 180
    if (swing_angle > max_rad && swing_angle > 1e-6) {
      const scale = max_rad / swing_angle
      TMP_NEW_SWING.copy(TMP_IDENTITY).slerp(TMP_SWING, scale)
    } else {
      TMP_NEW_SWING.copy(TMP_SWING)
    }
  } else {
    TMP_NEW_SWING.copy(TMP_SWING)
  }

  // Recombine: q = swing * twist (preserves the original decomposition order).
  quaternion.copy(TMP_NEW_SWING).multiply(TMP_NEW_TWIST)
}

function set_axis (out: Vector3, axis: 'x' | 'y' | 'z'): void {
  if (axis === 'x') out.set(1, 0, 0)
  else if (axis === 'z') out.set(0, 0, 1)
  else out.set(0, 1, 0)
}

/**
 * Swing-twist decomposition: split `q` into `twist` (rotation around
 * `twist_axis`) and `swing` (rotation perpendicular to it) such that
 * q = swing * twist. Standard formulation due to Dobrowolski (2015).
 */
function decompose_swing_twist (q: Quaternion, twist_axis: Vector3, out_swing: Quaternion, out_twist: Quaternion): void {
  // Project the vector part of q onto the twist axis.
  const dot = q.x * twist_axis.x + q.y * twist_axis.y + q.z * twist_axis.z
  const proj_x = twist_axis.x * dot
  const proj_y = twist_axis.y * dot
  const proj_z = twist_axis.z * dot
  out_twist.set(proj_x, proj_y, proj_z, q.w).normalize()
  TMP_TWIST_INV.copy(out_twist).invert()
  out_swing.copy(q).multiply(TMP_TWIST_INV)
}

/**
 * Rotate the chain around the root→end-effector axis so the chain's bend
 * plane (defined by root, immediate-parent of IK target, end-effector)
 * aligns with the user's pole direction. Resolves the rotation ambiguity
 * around the chain axis.
 */
const TMP_AXIS = new Vector3()
const TMP_ROOT_WORLD = new Vector3()
const TMP_MID_WORLD = new Vector3()
const TMP_END_WORLD_2 = new Vector3()
const TMP_MID_DIR = new Vector3()
const TMP_POLE_PROJ = new Vector3()
const TMP_POLE_DELTA = new Quaternion()
function apply_pole_alignment (chain: Bone[], ik_target_bone: Bone, pole_world_dir: Vector3): void {
  const root_bone = chain[chain.length - 1]
  const mid_bone = chain[0]
  root_bone.updateMatrixWorld(true)
  root_bone.getWorldPosition(TMP_ROOT_WORLD)
  mid_bone.updateMatrixWorld(true)
  mid_bone.getWorldPosition(TMP_MID_WORLD)
  ik_target_bone.updateMatrixWorld(true)
  ik_target_bone.getWorldPosition(TMP_END_WORLD_2)

  TMP_AXIS.copy(TMP_END_WORLD_2).sub(TMP_ROOT_WORLD)
  if (TMP_AXIS.lengthSq() < 1e-9) return
  TMP_AXIS.normalize()

  // Project (mid - root) onto the plane perpendicular to AXIS.
  TMP_MID_DIR.copy(TMP_MID_WORLD).sub(TMP_ROOT_WORLD)
  TMP_MID_DIR.addScaledVector(TMP_AXIS, -TMP_MID_DIR.dot(TMP_AXIS))
  if (TMP_MID_DIR.lengthSq() < 1e-9) return
  TMP_MID_DIR.normalize()

  TMP_POLE_PROJ.copy(pole_world_dir)
  if (TMP_POLE_PROJ.lengthSq() < 1e-9) return
  TMP_POLE_PROJ.normalize()
  TMP_POLE_PROJ.addScaledVector(TMP_AXIS, -TMP_POLE_PROJ.dot(TMP_AXIS))
  if (TMP_POLE_PROJ.lengthSq() < 1e-9) return
  TMP_POLE_PROJ.normalize()

  // Signed angle from MID_DIR to POLE_PROJ around AXIS.
  const cos = Math.max(-1, Math.min(1, TMP_MID_DIR.dot(TMP_POLE_PROJ)))
  const cross = new Vector3().crossVectors(TMP_MID_DIR, TMP_POLE_PROJ)
  const sin_sign = Math.sign(cross.dot(TMP_AXIS))
  const angle = Math.acos(cos) * (sin_sign === 0 ? 1 : sin_sign)
  if (Math.abs(angle) < 1e-5) return

  // Rotate the root bone by `angle` around AXIS in world space.
  TMP_POLE_DELTA.setFromAxisAngle(TMP_AXIS, angle)
  if (root_bone.parent !== null) {
    const parent_world_inv = new Quaternion()
    root_bone.parent.getWorldQuaternion(parent_world_inv).invert()
    const parent_world = new Quaternion().copy(parent_world_inv).invert()
    const delta_local = new Quaternion().copy(parent_world_inv).multiply(TMP_POLE_DELTA).multiply(parent_world)
    root_bone.quaternion.premultiply(delta_local)
  } else {
    root_bone.quaternion.premultiply(TMP_POLE_DELTA)
  }
  root_bone.updateMatrixWorld(true)
}
